import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  LinkBox,
  Text,
  useMediaQuery,
} from "@chakra-ui/react"
import { PrintServiceProductEntityHydrated } from "@jackfruit/common"
import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useProcessActions } from "~/hooks/useProcessActions"
import { useProductGridImage } from "~/hooks/useProductGridImage"

interface Props {
  product: PrintServiceProductEntityHydrated
  layout: "vertical" | "horizontal"
  index: number
  isCTAButtonEnabled: boolean
  ctaButtonText: string
  isPhotoBook: boolean
}

const ProductGridItem: React.FC<Props> = ({
  product,
  layout,
  index,
  isCTAButtonEnabled,
  ctaButtonText,
  isPhotoBook,
}) => {
  const { t } = useTranslation()

  const [isCustomLargeScreen] = useMediaQuery("(min-width: 1024px)");


  const { id: productId, shortDescription, longDescription } = product
  const process = useProcessActions()

  const { productMobileImage, productDesktopImage } =
    useProductGridImage(productId)

  const imageObjectFit =
    productMobileImage?.metaData?.web?.grid?.imageFillType || "cover"

  const isShowCTAButton = Boolean(isCTAButtonEnabled && layout === "horizontal")

  const handleSelectProduct = useCallback(() => {
    process.createLineItem({
      productId,
      createFromProduct: true,
    })
  }, [process, productId])

  const shouldShowLongDescription = longDescription && isPhotoBook

  return (
    <LinkBox
      role="group"
      bg="white"
      mx="auto"
      mb={4}
      rounded={{ base: "base", md: "md" }}
      overflow="hidden"
      boxShadow="md"
      cursor="pointer"
      onClick={handleSelectProduct}
      className={`product-grid-item product-grid-item-${layout} p-product-grid-item-${index}`}
      data-testid={product.id}
      transition="all 0.2s ease-in-out"
      _hover={{
        transform: "scale(1.03)",
        cursor: "pointer",
        boxShadow: "lg",
      }}
      backgroundColor="#f4f1f0"
      aria-label="product-grid-item"
      height={{ base: "134px", md: "auto" }}
      display={{ base: "flex", md: "block" }}
    >
      <Box
        className="product-grid-image"
        backgroundColor="#f4f1f0"
        display={{ base: "flex", md: "block" }}
        aspectRatio={{ base: 1, md: 700 / 440 }}
      >
        <Box
          as="picture"
          position="relative"
          width={{ base: "134px", md: "100%" }}
          height={{ base: "134px", md: "100%" }}
          display="block"
        >
          <Box
            as="source"
            media="(min-width: 768px)"
            srcSet={productDesktopImage?.thumbnail}
            width="700"
            height="440"
          />
          <Box
            as="source"
            srcSet={productMobileImage?.thumbnail}
            width="600"
            height="600"
          />
          <Image
            src={productMobileImage?.thumbnail}
            objectFit={imageObjectFit}
            borderTopLeftRadius="md"
            borderTopRightRadius={{ base: "0", md: "md" }}
            borderBottomLeftRadius={{ base: "md", md: "0" }}
            borderBottomRightRadius={{ base: "0", md: "0" }}
            alt={shortDescription}
            width="600"
            height="600"
            style={{ width: "100%", height: "100%" }}
          />
        </Box>
      </Box>

      <Flex
        direction="column"
        px={{ base: 3, md: 2, lg: 3 }}
        py={{ base: 2, md: 3 }}
        gap={{ base: 2, md: 3 }}
        justifyContent="space-between"
        w="full"
      >
        <Flex className="wrap-description"
          aria-label="wrap-description"
          direction="column"
          h={{ base: "auto", md: shouldShowLongDescription ? "129px" : "81px", xl: shouldShowLongDescription ? "102px" : "48px" }}>
          <Flex className="product-grid-name" >
            <Heading
              as="h3"
              fontSize={{ base: "md", md: "lg" }}
              textOverflow="ellipsis"
              lineHeight={1.5}
              noOfLines={isCustomLargeScreen ? 2 : 3}
            >
              {shortDescription}
            </Heading>

          </Flex>

          {shouldShowLongDescription && (
            <Flex
              className="long-description"
              aria-label="photobook-long-description"
              display={{ base: "none", md: "flex" }}
            >
              <Text textOverflow="ellipsis" noOfLines={2} color="gray.600" h="48px">
                {longDescription}
              </Text>
            </Flex>
          )}
        </Flex>

        {isShowCTAButton && (
          <Flex>
            <Button
              id={`cta-button--${productId}`}
              colorScheme="primary"
              color="primary.text"
              className="cta-button"
              size={{ base: "sm", md: "md" }}
              fontSize="1rem"
              w="full"
              _groupHover={{ display: "inline-block" }}
            >
              {ctaButtonText ||
                t(
                  "components.components.product-grid.ProductGridItem.CTAButtonDefault"
                )}
            </Button>
          </Flex>
        )}
      </Flex>
    </LinkBox>
  )
}

export default ProductGridItem
